import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "../providers/auth-context";
import type { RouterContext } from "../router-context";
import { useAdminApi } from "../providers/api-context";
import { jstToday } from "../lib/format";
import { useFleetRealtimeInvalidation } from "../lib/use-fleet-realtime-invalidation";
import { Button } from "@carry/ui";
import { notificationTypeLabel } from "@carry/contract";
import { MASTER_MENU_ITEMS, type MasterMenuPath, type MasterNavKey } from "../lib/master-menu";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

// ────────────────────────────────────────────────────────
// Types & constants (shell design — carry-design/carry-pilot のプロトタイプを参考)
// ────────────────────────────────────────────────────────

type Density = "compact" | "balanced" | "spacious";

type NavKey =
  | "dashboard"
  | "orders"
  | "routes"
  | "status"
  | "status-read"
  | "reports"
  | "work-reports"
  | "expenses"
  | "attendance"
  | "billing"
  | MasterNavKey
  | "news"
  | "line-logs"
  | "settings"
  | "notifications";

type IconFn = (size?: number, color?: string) => ReactNode;

const I: Record<string, IconFn> = {
  dash: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" stroke={c} strokeWidth="1.2" />
      <rect x="9" y="2" width="5" height="5" stroke={c} strokeWidth="1.2" />
      <rect x="2" y="9" width="5" height="5" stroke={c} strokeWidth="1.2" />
      <rect x="9" y="9" width="5" height="5" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  order: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M3 2h7l3 3v9H3V2z" stroke={c} strokeWidth="1.2" />
      <path d="M10 2v3h3" stroke={c} strokeWidth="1.2" />
      <path d="M5 8h6M5 11h4" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  route: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="4" cy="4" r="2" stroke={c} strokeWidth="1.2" />
      <circle cx="12" cy="12" r="2" stroke={c} strokeWidth="1.2" />
      <path d="M4 6v2a2 2 0 002 2h4a2 2 0 012 2" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  map: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M2 4l4-2 4 2 4-2v10l-4 2-4-2-4 2V4z" stroke={c} strokeWidth="1.2" />
      <path d="M6 2v10M10 4v10" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  report: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="2" width="11" height="12" stroke={c} strokeWidth="1.2" />
      <path d="M5 5h6M5 8h6M5 11h4" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  // 勤怠是正: 時計
  clock: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke={c} strokeWidth="1.2" />
      <path d="M8 4.5V8l3 1.8" stroke={c} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  ruler: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="5" width="14" height="6" stroke={c} strokeWidth="1.2" />
      <path d="M4 5v2M7 5v3M10 5v2M13 5v3" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  bill: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M3 1h10v14l-2-1-2 1-2-1-2 1-2-1V1z" stroke={c} strokeWidth="1.2" />
      <path d="M6 5h4M6 8h4M6 11h2" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  people: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke={c} strokeWidth="1.2" />
      <path d="M1.5 14c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke={c} strokeWidth="1.2" />
      <circle cx="11.5" cy="4.5" r="2" stroke={c} strokeWidth="1.2" />
      <path d="M10.5 10c2 0 4 1 4 3.5" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  truck: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="5" width="8" height="6" stroke={c} strokeWidth="1.2" />
      <path d="M9 7h3l2 2v2H9z" stroke={c} strokeWidth="1.2" />
      <circle cx="4" cy="12" r="1.3" stroke={c} strokeWidth="1.2" />
      <circle cx="11.5" cy="12" r="1.3" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  line: (s = 16, c = "#06c755") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect width="16" height="16" rx="3.5" fill={c} />
      <path
        d="M8 3C4.7 3 2 5 2 7.5c0 2.2 2 4 4.7 4.4.2 0 .4.2.4.3v1.4c.1.2.3.3.5.1l2.7-2c1.9-.5 3.7-2.2 3.7-4.2C14 5 11.3 3 8 3z"
        fill="#fff"
      />
    </svg>
  ),
  search: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="4.5" stroke={c} strokeWidth="1.2" />
      <path d="M10.5 10.5l3 3" stroke={c} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  bell: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path
        d="M3.5 11.5h9l-1.2-1.5V7a3.3 3.3 0 00-6.6 0v3L3.5 11.5z"
        stroke={c}
        strokeWidth="1.2"
      />
      <path d="M6.5 13.5a1.5 1.5 0 003 0" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  gear: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.2" stroke={c} strokeWidth="1.2" />
      <path
        d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4"
        stroke={c}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  // 指示確認状況: 目(既読確認)
  eye: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke={c} strokeWidth="1.2" />
      <circle cx="8" cy="8" r="2" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  // 取引先: ビル(企業)
  building: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M1 14h14M3 14V4l5-2 5 2v10" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      <rect x="6.5" y="9" width="3" height="5" stroke={c} strokeWidth="1.2" />
      <path
        d="M5.5 6.5h1.5M9 6.5h1.5M5.5 8.5h1.5M9 8.5h1.5"
        stroke={c}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  // 納品先: ピン(配送先)
  pin: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path
        d="M8 14.5S3 9.2 3 5.8a5 5 0 0110 0C13 9.2 8 14.5 8 14.5z"
        stroke={c}
        strokeWidth="1.2"
      />
      <circle cx="8" cy="5.8" r="1.8" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  // 集荷拠点: 倉庫
  warehouse: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M1 7.5L8 2l7 5.5V14H1V7.5z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      <rect x="5.5" y="9.5" width="5" height="4.5" stroke={c} strokeWidth="1.2" />
      <path d="M8 9.5V14" stroke={c} strokeWidth="1.2" />
    </svg>
  ),
  // ユーザー: 一人
  user: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke={c} strokeWidth="1.2" />
      <path
        d="M2 14c0-2.8 2.7-5 6-5s6 2.2 6 5"
        stroke={c}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  // 経費: レシート
  receipt: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path
        d="M3 2h10v13l-2-1.5-1.5 1.5L8 14l-1.5-1.5L5 14 3 15V2z"
        stroke={c}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M5.5 6h5M5.5 9h5M5.5 12h3" stroke={c} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  megaphone: (s = 16, c = "currentColor") => (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M2 7v2h3l6 3V4L5 7H2z" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M5 9l1 4h2L7 10" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
};

type NavPath =
  | "/"
  | "/orders"
  | "/routes"
  | "/status"
  | "/status/read"
  | "/reports"
  | "/reports/monthly"
  | "/expenses"
  | "/attendance"
  | "/billing"
  | MasterMenuPath
  | "/news"
  | "/line-logs"
  | "/settings"
  | "/notifications";

interface NavItem {
  key: NavKey;
  label: string;
  icon: IconFn;
  path: NavPath;
  group: "overview" | "ops" | "finance" | "masters" | "admin";
}

/** ナビバッジの実数 (0 件はバッジ非表示). alert=true は赤バッジ. */
interface NavBadge {
  count: number;
  alert?: boolean;
}
type NavBadgeMap = Partial<Record<NavKey, NavBadge>>;

const MASTER_NAV_ICONS: Record<MasterNavKey, IconFn> = {
  "masters-clients": I.building!,
  "masters-deliverySpots": I.pin!,
  "masters-sites": I.warehouse!,
  "masters-users": I.user!,
  vehicles: I.truck!,
};

const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", label: "ダッシュボード", icon: I.dash!, path: "/", group: "overview" },
  { key: "orders", label: "受注管理", icon: I.order!, path: "/orders", group: "ops" },
  { key: "routes", label: "ルート作成", icon: I.route!, path: "/routes", group: "ops" },
  { key: "status", label: "ドライバー状況", icon: I.map!, path: "/status", group: "ops" },
  {
    key: "status-read",
    label: "指示確認状況",
    icon: I.eye!,
    path: "/status/read",
    group: "ops",
  },
  {
    key: "reports",
    label: "運転日報",
    icon: I.report!,
    path: "/reports",
    group: "ops",
  },
  {
    key: "expenses",
    label: "経費管理",
    icon: I.receipt!,
    path: "/expenses",
    group: "ops",
  },
  {
    key: "attendance",
    label: "勤怠是正",
    icon: I.clock!,
    path: "/attendance",
    group: "ops",
  },
  {
    key: "work-reports",
    label: "業務日報",
    icon: I.report!,
    path: "/reports/monthly",
    group: "finance",
  },
  { key: "billing", label: "請求管理", icon: I.bill!, path: "/billing", group: "finance" },
  ...MASTER_MENU_ITEMS.map(
    (item): NavItem => ({
      key: item.navKey,
      label: item.navLabel,
      icon: MASTER_NAV_ICONS[item.navKey],
      path: item.path,
      group: "masters",
    }),
  ),
  { key: "news", label: "LINEメッセージ送信", icon: I.megaphone!, path: "/news", group: "admin" },
  { key: "line-logs", label: "LINEログ", icon: I.line!, path: "/line-logs", group: "admin" },
  { key: "settings", label: "システム設定", icon: I.gear!, path: "/settings", group: "admin" },
];

type AdminUserRole = "admin" | "office_staff" | "driver" | string | null | undefined;
const ADMIN_ONLY_NAV_KEYS = new Set<NavKey>([
  ...MASTER_MENU_ITEMS.filter((item) => item.adminOnly).map((item) => item.navKey),
  "settings",
]);
const DISPATCHER_ONLY_NAV_KEYS = new Set<NavKey>([
  ...MASTER_MENU_ITEMS.filter((item) => item.dispatcherOnly).map((item) => item.navKey),
  "news",
  "line-logs",
]);

export function getVisibleNavItems(userRole: AdminUserRole): NavItem[] {
  if (userRole === "admin") return NAV_ITEMS;
  return NAV_ITEMS.filter(
    (item) =>
      !ADMIN_ONLY_NAV_KEYS.has(item.key) &&
      (!DISPATCHER_ONLY_NAV_KEYS.has(item.key) || userRole === "office_staff"),
  );
}

export function requireAdminRoute({ context }: { context: RouterContext }) {
  if (context.authLoading) return;
  if (!context.session) throw redirect({ to: "/auth/login" });
  if (context.session.user.role !== "admin") throw redirect({ to: "/" });
}

export function requireStaffManagementRoute({ context }: { context: RouterContext }) {
  if (context.authLoading) return;
  if (!context.session) throw redirect({ to: "/auth/login" });
  if (context.session.user.role !== "admin" && context.session.user.role !== "office_staff") {
    throw redirect({ to: "/" });
  }
}

const GROUPS: { key: NavItem["group"]; label: string }[] = [
  { key: "overview", label: "" },
  { key: "ops", label: "オペレーション" },
  { key: "finance", label: "財務" },
  { key: "masters", label: "マスタ" },
  { key: "admin", label: "管理" },
];

const BREADCRUMBS: Record<string, string[]> = {
  "/": ["ダッシュボード"],
  "/orders": ["オペレーション", "受注管理"],
  "/orders/new": ["オペレーション", "受注管理", "新規作成"],
  "/parse-jobs": ["オペレーション", "解析ジョブ"],
  "/routes": ["オペレーション", "ルート作成"],
  "/status": ["オペレーション", "ドライバー状況"],
  "/status/read": ["オペレーション", "指示確認状況"],
  "/alerts": ["ダッシュボード", "アラート履歴"],
  "/reports": ["オペレーション", "運転日報"],
  "/reports/monthly": ["財務", "業務日報"],
  "/expenses": ["オペレーション", "経費管理"],
  "/attendance": ["オペレーション", "勤怠是正"],
  "/billing": ["財務", "請求管理"],
  "/billing/new": ["財務", "請求管理", "新規作成"],
  "/settings": ["システム設定"],
  "/masters/clients": ["マスタ", "取引先"],
  "/masters/deliverySpots": ["マスタ", "納品先"],
  "/masters/sites": ["マスタ", "集荷拠点"],
  "/masters/users": ["マスタ", "ユーザー管理"],
  "/vehicles": ["マスタ", "車両管理"],
  "/notifications": ["通知一覧"],
  "/news": ["LINEメッセージ送信"],
  "/line-logs": ["管理", "LINEログ"],
  "/profile": ["Myプロフィール設定"],
};

/**
 * pathname からパンくずラベル配列を解決する。
 * 静的マップ完全一致 → 動的詳細パスの個別対応 → 安全な既定ラベルの順。
 * 動的詳細ルートや未定義パスでは pathname / public_id を表示しない。
 */
export function resolveBreadcrumb(pathname: string): string[] {
  if (BREADCRUMBS[pathname]) return BREADCRUMBS[pathname]!;
  if (/^\/orders\/[^/]+\/edit$/.test(pathname)) return ["オペレーション", "受注管理", "編集"];
  if (/^\/orders\/[^/]+$/.test(pathname)) return ["オペレーション", "受注管理", "詳細"];
  // /billing/new は BREADCRUMBS で完全一致。それ以外の /billing/:id は請求詳細。
  if (/^\/billing\/[^/]+$/.test(pathname)) return ["財務", "請求管理", "詳細"];
  return ["ページ"];
}

// ────────────────────────────────────────────────────────
// Root layout
// ────────────────────────────────────────────────────────
function RootLayout() {
  const { session, loading, sessionExpired, signOut } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const userStatus = session?.user?.status;
  const userRole = session?.user?.role;
  const requiresInvite = Boolean(
    session && userStatus && userStatus !== "active" && userRole !== "admin",
  );
  useFleetRealtimeInvalidation({
    apiBaseUrl: API_BASE_URL,
    enabled: Boolean(session) && !requiresInvite,
  });
  const navigate = useNavigate();
  const [driverView, setDriverView] = useState(false);
  const [density, setDensity] = useState<Density>("balanced");
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [explicitSignOutInProgress, setExplicitSignOutInProgress] = useState(false);
  const explicitSignOutInProgressRef = useRef(false);

  useEffect(() => {
    document.body.setAttribute("data-density", density);
  }, [density]);

  // status='pending' の user は /auth/invite に誘導 (admin は招待フロー不要のためスキップ)
  useEffect(() => {
    if (loading) return;
    const isAuth = pathname.startsWith("/auth");
    if (!session && !isAuth) {
      if (explicitSignOutInProgressRef.current) return;
      void navigate(
        sessionExpired
          ? { to: "/auth/login" }
          : { to: "/auth/login", search: { error: "no_session" } as { error: string } },
      );
      return;
    }
    if (requiresInvite && pathname !== "/auth/invite") {
      void navigate({ to: "/auth/invite" });
    }
  }, [
    loading,
    session,
    sessionExpired,
    explicitSignOutInProgress,
    requiresInvite,
    userStatus,
    userRole,
    pathname,
    navigate,
  ]);

  async function handleSignOut() {
    explicitSignOutInProgressRef.current = true;
    setExplicitSignOutInProgress(true);
    try {
      await signOut();
      await navigate({ to: "/auth/login" });
    } catch {
      toast.error("ログアウトに失敗しました");
    } finally {
      explicitSignOutInProgressRef.current = false;
      setExplicitSignOutInProgress(false);
    }
  }

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const data = e.data as { type?: string } | null;
      if (data?.type === "__activate_edit_mode") setTweaksOpen(true);
      if (data?.type === "__deactivate_edit_mode") setTweaksOpen(false);
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ type: "__edit_mode_available" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // フックは early return (loading / auth / driverView) より前に置く。
  // 条件分岐の後に置くとレンダー間でフック数が変わり React #310 になる。
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("cp-sidebar-open", sidebarOpen);
    return () => document.body.classList.remove("cp-sidebar-open");
  }, [sidebarOpen]);

  // ページ遷移時にサイドバーを閉じる
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          fontFamily: "var(--f-sans)",
          color: "var(--ink-3)",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            border: "2px solid var(--rule-soft)",
            borderTopColor: "var(--accent)",
            borderRadius: "50%",
            animation: "cp-spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes cp-spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ fontSize: 13 }}>読み込み中…</span>
      </div>
    );
  }

  const isAuthRoute = pathname.startsWith("/auth");
  if (requiresInvite && pathname !== "/auth/invite") {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--f-sans)",
          color: "var(--ink-3)",
        }}
      >
        招待コード入力画面へ移動中…
      </div>
    );
  }

  // 未ログインまたは /auth/* のときは shell を出さず Outlet のみ
  if (!session || isAuthRoute) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
        <Outlet />
        {tweaksOpen && (
          <TweaksPanel
            density={density}
            setDensity={setDensity}
            driverView={driverView}
            setDriverView={setDriverView}
          />
        )}
      </div>
    );
  }

  if (driverView) {
    return (
      <>
        <DriverPlaceholder onBack={() => setDriverView(false)} />
        {tweaksOpen && (
          <TweaksPanel
            density={density}
            setDensity={setDensity}
            driverView={driverView}
            setDriverView={setDriverView}
          />
        )}
      </>
    );
  }

  const breadcrumb = resolveBreadcrumb(pathname);

  return (
    <div style={{ display: "flex" }}>
      <Sidebar
        currentPath={pathname}
        userName={session.user.name ?? null}
        userRole={session.user.role}
        onSignOut={() => void handleSignOut()}
      />
      {/* モバイル: サイドバー開時の背景オーバーレイ */}
      <div className="cp-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Topbar breadcrumb={breadcrumb} onToggleSidebar={() => setSidebarOpen((v) => !v)} />
        <div style={{ flex: 1, minWidth: 0, padding: 24 }}>
          <Outlet />
        </div>
      </main>
      {tweaksOpen && (
        <TweaksPanel
          density={density}
          setDensity={setDensity}
          driverView={driverView}
          setDriverView={setDriverView}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Sidebar
// ────────────────────────────────────────────────────────
/**
 * ナビバッジを実データから集計する.
 * - orders: 当日の未確定 (draft) 受注件数
 * - reports: 走行距離乖離アラート件数 (赤バッジ, #627 で距離突合から運転日報へ移設)
 * - attendance: 当日の退勤打刻忘れ件数 (赤バッジ, 整合 L-1)
 * 取得できない / 0 件の場合はバッジを出さない (固定ダミー値を排除).
 */
function useNavBadges(): NavBadgeMap {
  const { session } = useAuth();
  const api = useAdminApi();
  const today = jstToday();

  const statsQuery = useQuery({
    queryKey: ["nav-badges", "stats", session?.user?.id, today],
    queryFn: () => api.dashboard.stats({ date: today }),
    enabled: !!session,
    refetchInterval: 60_000,
  });
  const alertsQuery = useQuery({
    queryKey: ["nav-badges", "alerts", session?.user?.id],
    queryFn: () => api.dashboard.alerts({ limit: 50 }),
    enabled: !!session,
    refetchInterval: 60_000,
  });
  const attendanceQuery = useQuery({
    queryKey: ["nav-badges", "attendance", session?.user?.id, today],
    queryFn: () => api.attendance.list({ from: today, to: today, limit: 200 }),
    enabled: !!session,
    refetchInterval: 60_000,
  });

  const stats = statsQuery.data?.data;
  const deviationCount = alertsQuery.data?.data.filter((a) => a.kind === "deviation").length ?? 0;
  const missingClockOutCount =
    attendanceQuery.data?.data.filter((a) => a.clock_out_at == null || a.odometer_end == null)
      .length ?? 0;

  const badges: NavBadgeMap = {};
  if (stats && stats.status_breakdown.draft > 0) {
    badges.orders = { count: stats.status_breakdown.draft };
  }
  if (missingClockOutCount > 0) {
    badges.attendance = { count: missingClockOutCount, alert: true };
  }
  if (deviationCount > 0) {
    badges.reports = { count: deviationCount, alert: true };
  }
  return badges;
}

function Sidebar({
  currentPath,
  userName,
  userRole,
  onSignOut,
}: {
  currentPath: string;
  userName: string | null;
  userRole: AdminUserRole;
  onSignOut: () => void;
}) {
  const initial = userName?.trim().charAt(0).toUpperCase() || "?";
  const badges = useNavBadges();
  const activeKey = (() => {
    let best: NavItem | null = null;
    for (const item of NAV_ITEMS) {
      if (item.path === "/") {
        if (currentPath === "/") best = item;
        continue;
      }
      if (currentPath === item.path || currentPath.startsWith(item.path + "/")) {
        if (!best || item.path.length > best.path.length) best = item;
      }
    }
    return best?.key ?? null;
  })();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handle(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [menuOpen]);

  return (
    <aside
      className="cp-sidebar"
      style={{
        width: 224,
        flexShrink: 0,
        borderRight: "0.5px solid var(--rule)",
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      <div
        style={{
          padding: "10px 20px",
          borderBottom: "0.5px solid var(--rule)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src="/images/system-logo.png"
          alt="CarryPilot"
          style={{ width: 28, height: 28, display: "block" }}
        />
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em" }}>
            Carry<span style={{ color: "var(--accent)" }}>Pilot</span>
          </div>
        </div>
      </div>

      <nav style={{ padding: "12px 8px", flex: 1, overflowY: "auto" }}>
        {GROUPS.map((g) => {
          const items = getVisibleNavItems(userRole).filter((it) => it.group === g.key);
          if (items.length === 0) return null;
          return (
            <div key={g.key} style={{ marginBottom: 16 }}>
              {g.label && (
                <div className="eyebrow" style={{ padding: "4px 12px 6px" }}>
                  {g.label}
                </div>
              )}
              {items.map((it) => (
                <SidebarItem key={it.key} item={it} activeKey={activeKey} badge={badges[it.key]} />
              ))}
            </div>
          );
        })}
      </nav>

      {/* ユーザーメニュー */}
      <div
        ref={menuRef}
        style={{
          padding: "8px 10px",
          borderTop: "0.5px solid var(--rule-soft)",
          position: "relative",
        }}
      >
        {menuOpen && (
          <div
            style={{
              position: "absolute",
              left: "calc(100% + 8px)",
              bottom: 0,
              width: 220,
              background: "var(--bg-raised)",
              border: "0.5px solid var(--rule-soft)",
              borderRadius: 8,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              overflow: "hidden",
              zIndex: 200,
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "0.5px solid var(--rule-soft)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: "var(--bg-sunken)",
                  color: "var(--ink-2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initial}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {userName && (
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {userName}
                  </div>
                )}
              </div>
            </div>
            <Link
              to="/profile"
              onClick={() => setMenuOpen(false)}
              onMouseEnter={() => setHoveredItem("profile")}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                color: "var(--ink)",
                textDecoration: "none",
                fontSize: 13,
                background: hoveredItem === "profile" ? "var(--bg-sunken)" : "transparent",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2" />
                <path
                  d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.36 3.64l-1.06 1.06M4.7 11.3l-1.06 1.06M12.36 12.36l-1.06-1.06M4.7 4.7l-1.06-1.06"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              プロフィール設定
            </Link>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onSignOut();
              }}
              onMouseEnter={() => setHoveredItem("logout")}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                width: "100%",
                background: hoveredItem === "logout" ? "var(--bg-sunken)" : "transparent",
                border: "none",
                borderTop: "0.5px solid var(--rule-softer)",
                color: "var(--ink)",
                fontSize: 13,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path
                  d="M5.5 2H3v11h2.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                <path
                  d="M10 5l2.5 2.5L10 10"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.5 7.5H6"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              ログアウト
            </button>
          </div>
        )}

        <button
          type="button"
          aria-label="ユーザーメニュー"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "8px 10px",
            background: menuOpen ? "var(--bg-sunken)" : "transparent",
            border: "0.5px solid var(--rule-soft)",
            borderRadius: 8,
            cursor: "pointer",
            color: "inherit",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "var(--bg-sunken)",
              color: "var(--ink-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initial}
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            {userName && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </div>
            )}
          </div>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            style={{ flexShrink: 0, color: "var(--ink-4)" }}
          >
            <path
              d="M4 5l3-2.5L10 5"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 9l3 2.5 3-2.5"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </aside>
  );
}

function SidebarItem({
  item,
  activeKey,
  badge,
}: {
  item: NavItem;
  activeKey: string | null;
  badge?: NavBadge;
}) {
  const active = item.key === activeKey;
  return (
    <Link
      to={item.path}
      search={{}}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "7px 12px",
        background: active ? "var(--bg-raised)" : "transparent",
        border: active ? "0.5px solid var(--rule)" : "0.5px solid transparent",
        borderRadius: 2,
        color: active ? "var(--ink)" : "var(--ink-2)",
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        marginBottom: 1,
        position: "relative",
        textDecoration: "none",
      }}
    >
      {active && (
        <span
          style={{
            position: "absolute",
            left: -8,
            top: 4,
            bottom: 4,
            width: 2,
            background: "var(--accent)",
          }}
        />
      )}
      <span style={{ color: active ? "var(--ink)" : "var(--ink-3)" }}>{item.icon(15)}</span>
      <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
      {badge && badge.count > 0 && (
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            padding: "1px 5px",
            borderRadius: 2,
            background: badge.alert ? "var(--danger)" : "var(--ink)",
            color: "#fff",
          }}
        >
          {badge.count}
        </span>
      )}
    </Link>
  );
}

// ────────────────────────────────────────────────────────
// Topbar
// ────────────────────────────────────────────────────────

function formatRelativeShort(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "たった今";
  if (mins < 60) return `${mins}分前`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}時間前`;
  return `${Math.floor(hrs / 24)}日前`;
}

function Topbar({
  breadcrumb,
  onToggleSidebar,
}: {
  breadcrumb: string[];
  onToggleSidebar: () => void;
}) {
  const [notifOpen, setNotifOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const { session } = useAuth();

  const notifQuery = useQuery({
    queryKey: ["notifications", "topbar", session?.user?.id],
    queryFn: () => api.notification.list({ limit: 10 }),
    enabled: !!session,
    refetchInterval: 30_000,
  });
  const markReadMutation = useMutation({
    mutationFn: (publicId: string) => api.notification.markRead({ publicId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const notifications = notifQuery.data?.data ?? [];
  const unreadCount = notifQuery.data?.unread_count ?? 0;

  useEffect(() => {
    if (!notifOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setNotifOpen(false);
        bellRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [notifOpen]);

  return (
    <div
      className="cp-toolbar"
      style={{
        height: 48,
        flexShrink: 0,
        borderBottom: "0.5px solid var(--rule)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 24px 0 32px",
        background: "var(--bg)",
        position: "sticky",
        top: 0,
        zIndex: 40,
      }}
    >
      <button
        type="button"
        className="cp-hamburger"
        onClick={onToggleSidebar}
        aria-label="メニューを開く"
        style={{
          width: 36,
          height: 36,
          border: "0.5px solid var(--rule-soft)",
          borderRadius: 2,
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg-raised)",
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M2 4h12M2 8h12M2 12h12"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <nav
        aria-label="パンくずリスト"
        style={{
          fontSize: 12,
          color: "var(--ink-3)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {breadcrumb.map((b, i) => {
          const isLast = i === breadcrumb.length - 1;
          return (
            <span key={`${i}-${b}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {i > 0 && <span style={{ color: "var(--ink-4)" }}>/</span>}
              <span
                style={{
                  color: isLast ? "var(--ink)" : "var(--ink-3)",
                  fontWeight: isLast ? 500 : 400,
                }}
              >
                {b}
              </span>
            </span>
          );
        })}
      </nav>
      <div style={{ flex: 1 }} />
      <div style={{ position: "relative" }}>
        <button
          ref={bellRef}
          type="button"
          aria-label={unreadCount > 0 ? `通知 (未読 ${unreadCount} 件)` : "通知"}
          aria-haspopup="dialog"
          aria-expanded={notifOpen}
          onClick={() => setNotifOpen((o) => !o)}
          style={{
            width: 36,
            height: 36,
            border: "0.5px solid var(--rule-soft)",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--bg-raised)",
            position: "relative",
          }}
        >
          {I.bell!(14)}
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                minWidth: 14,
                height: 14,
                padding: "0 4px",
                borderRadius: 7,
                background: "var(--danger)",
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--f-mono)",
                lineHeight: 1,
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {notifOpen && (
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 49 }}
              onClick={() => {
                setNotifOpen(false);
                bellRef.current?.focus();
              }}
            />
            <div
              role="dialog"
              aria-label="通知"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                width: 360,
                background: "var(--bg-panel)",
                border: "0.5px solid var(--rule)",
                borderRadius: 3,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                zIndex: 50,
                maxHeight: "min(540px, 80vh)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "0.5px solid var(--rule-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  通知
                  {unreadCount > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: "var(--ink-3)" }}>
                      未読 {unreadCount} 件
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="通知を閉じる"
                  onClick={() => {
                    setNotifOpen(false);
                    bellRef.current?.focus();
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 14,
                    cursor: "pointer",
                    color: "var(--ink-3)",
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {notifQuery.isLoading && (
                  <div
                    style={{
                      padding: "32px 16px",
                      textAlign: "center",
                      fontSize: 12,
                      color: "var(--ink-3)",
                    }}
                  >
                    読み込み中…
                  </div>
                )}
                {!notifQuery.isLoading && notifications.length === 0 && (
                  <div
                    style={{
                      padding: "32px 16px",
                      textAlign: "center",
                      fontSize: 12,
                      color: "var(--ink-3)",
                    }}
                  >
                    新しい通知はありません
                  </div>
                )}
                {!notifQuery.isLoading &&
                  notifications.map((n, i) => {
                    const target = n.target?.kind === "order" ? n.target : null;
                    const content = (
                      <>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 6,
                            height: 6,
                            marginTop: 6,
                            borderRadius: "50%",
                            background: n.read_at ? "var(--rule-soft)" : "var(--accent)",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: 2,
                              fontSize: 10,
                              color: "var(--ink-3)",
                            }}
                          >
                            <span style={{ fontFamily: "var(--f-mono)" }}>
                              {notificationTypeLabel(n.type)}
                            </span>
                            <span>·</span>
                            <span>{formatRelativeShort(n.created_at)}</span>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: n.read_at ? 400 : 500,
                              marginBottom: 2,
                            }}
                          >
                            {n.title}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--ink-2)" }}>{n.body}</div>
                          {target && (
                            <div style={{ marginTop: 4, fontSize: 11, color: "var(--accent)" }}>
                              対象注文: {target.order_number ?? target.public_id}
                            </div>
                          )}
                        </div>
                      </>
                    );
                    return (
                      <div
                        key={n.id}
                        style={{
                          padding: "10px 16px",
                          borderBottom:
                            i < notifications.length - 1 ? "0.5px solid var(--rule-soft)" : "none",
                          background: n.read_at
                            ? "transparent"
                            : "var(--accent-wash, oklch(0.97 0.02 250))",
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                        }}
                      >
                        {target ? (
                          <Link
                            to="/orders/$publicId"
                            params={{ publicId: target.public_id }}
                            aria-label={`対象注文: ${target.order_number ?? target.public_id} の詳細を開く`}
                            onClick={() => {
                              if (!n.read_at) markReadMutation.mutate(n.id);
                              setNotifOpen(false);
                            }}
                            style={{
                              display: "flex",
                              gap: 10,
                              flex: 1,
                              color: "inherit",
                              textDecoration: "none",
                            }}
                          >
                            {content}
                          </Link>
                        ) : (
                          content
                        )}
                      </div>
                    );
                  })}
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  borderTop: "0.5px solid var(--rule-soft)",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Link
                  to="/notifications"
                  onClick={() => setNotifOpen(false)}
                  style={{ fontSize: 11, color: "var(--accent)", textDecoration: "none" }}
                >
                  すべての通知を見る →
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// TweaksPanel (Claude Design host protocol 連携)
// ────────────────────────────────────────────────────────
function TweaksPanel({
  density,
  setDensity,
  driverView,
  setDriverView,
}: {
  density: Density;
  setDensity: (d: Density) => void;
  driverView: boolean;
  setDriverView: (v: boolean) => void;
}) {
  const persist = (edits: Record<string, unknown>) => {
    window.parent.postMessage({ type: "__edit_mode_set_keys", edits }, "*");
  };
  const densities: Density[] = ["compact", "balanced", "spacious"];
  return (
    <div
      role="region"
      aria-label="Tweaks パネル"
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 100,
        width: 280,
        background: "var(--bg-raised)",
        border: "0.5px solid var(--rule)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
        borderRadius: 3,
        fontFamily: "var(--f-sans)",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "0.5px solid var(--rule-soft)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-.005em" }}>Tweaks</div>
        <span className="eyebrow">CarryPilot</span>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            情報密度
          </div>
          <div style={{ display: "flex", border: "0.5px solid var(--rule-soft)", borderRadius: 2 }}>
            {densities.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setDensity(d);
                  persist({ density: d });
                }}
                aria-pressed={d === density}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  fontSize: 11,
                  background: d === density ? "var(--ink)" : "transparent",
                  color: d === density ? "#fff" : "var(--ink-2)",
                  borderLeft: i > 0 ? "0.5px solid var(--rule-soft)" : "none",
                }}
              >
                {d === "compact" ? "Compact" : d === "balanced" ? "Balanced" : "Spacious"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            表示モード
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 10px",
              background: "var(--bg-panel)",
              border: "0.5px solid var(--rule-soft)",
              borderRadius: 2,
              fontSize: 12,
            }}
          >
            <span>ドライバーApp 表示</span>
            <input
              type="checkbox"
              checked={driverView}
              onChange={(e) => {
                setDriverView(e.target.checked);
                persist({ showDriverApp: e.target.checked });
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// DriverApp プレースホルダ
// ────────────────────────────────────────────────────────
function DriverPlaceholder({ onBack }: { onBack: () => void }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f0" }}>
      <div
        style={{
          height: 48,
          borderBottom: "0.5px solid var(--rule)",
          display: "flex",
          alignItems: "center",
          padding: "0 24px",
          gap: 12,
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 24,
              height: 24,
              background: "#06c755",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            D
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>ドライバーApp</div>
            <div style={{ fontSize: 10, color: "var(--ink-3)" }}>
              LINE ミニアプリ · 鈴木 一郎 さんの画面
            </div>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          ← 管理画面に戻る
        </Button>
      </div>
      <div style={{ padding: 64, textAlign: "center" }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>
          Coming soon
        </div>
        <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>ドライバーApp モック</div>
        <div style={{ fontSize: 13, color: "var(--ink-3)", maxWidth: 440, margin: "0 auto" }}>
          apps/driver で実装します。ここでは管理画面からのプレビュー切替を示しています。
        </div>
      </div>
    </div>
  );
}
